/**
 * Spec 061: a client's documents — every proposal, contract, act and their versions across its
 * orders, with the file the client got and its signed copy (Kostya, 31.10: «все договоры, кп и
 * версии договоров должны храниться в карточке клиента»).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";

import { ClientChat, Clients, type DocumentOut, type FileRef } from "@/api/orders";
import { Button } from "@/components/ui/Button";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { ago, EmptyNote } from "@/kit";
import { DOCUMENT_STATE, documentName } from "@/lib/orders";

/** A file of the order, saved under its own name. */
export function useFileDownload() {
  const toast = useToast();
  return useMutation({
    mutationFn: async (file: FileRef) => ({ file, blob: await ClientChat.file(file.id) }),
    onSuccess: ({ file, blob }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** What went to the client and what came back signed, each a download. */
export function DocumentFiles({ doc }: { doc: DocumentOut }) {
  const download = useFileDownload();
  const files: { file: FileRef; label: string }[] = [];
  if (doc.file) files.push({ file: doc.file, label: `Файл клиенту${doc.sent_at ? `, ${ago(doc.sent_at)}` : ""}` });
  if (doc.signed_file) files.push({ file: doc.signed_file, label: `Подписанный клиентом${doc.signed_at ? `, ${ago(doc.signed_at)}` : ""}` });
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {files.map(({ file, label }) => (
        <Button key={file.id} size="compact" variant="ghost" icon={<Download className="h-4 w-4" aria-hidden="true" />}
          loading={download.isPending && download.variables?.id === file.id} onClick={() => download.mutate(file)}>
          {label}
        </Button>
      ))}
    </div>
  );
}

/** The client's page: its documents, the newest order first. */
export function ClientDocumentsBlock({ clientId }: { clientId: string }) {
  const docs = useQuery({ queryKey: ["client", clientId, "documents"], queryFn: () => Clients.documents(clientId) });
  if (docs.isLoading) return <SkeletonList count={2} />;
  if (docs.isError) return <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void docs.refetch()}>Повторить</Button>}>Документы не загрузились.</EmptyNote>;
  if (!docs.data?.length) return <EmptyNote>Документов пока нет: КП, договоры и акты появятся здесь из заказов.</EmptyNote>;
  return (
    <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
      {docs.data.map((d) => (
        <li key={d.id} className="flex flex-col gap-1 border-b border-p-border px-4 py-3 last:border-0">
          <span className="flex flex-wrap items-baseline gap-2">
            <span className="font-semibold text-p-text">{documentName(d.kind, d.revision)}</span>
            <Mark>{DOCUMENT_STATE[d.state]}</Mark>
            <Link to={`/orders/${d.order.id}`} className="ml-auto text-meta text-p-link">{d.order.label}</Link>
          </span>
          <span className="text-body text-p-text">{d.title}</span>
          <span className="text-meta text-p-text-muted">
            {d.order.title}{d.decided_by ? ` · ${d.state === "rejected" ? "отклонил" : "утвердил"}: ${d.decided_by.name}` : ""}
          </span>
          <DocumentFiles doc={d} />
        </li>
      ))}
    </ul>
  );
}
